<template>
	<div>
		<div class="flex xs-12 xs-between mt6">
			<div class="fs4 lh125 fw7 c1 mb2">{{ $t("addresses") }}</div>
			<j-button
				v-if="hasActions"
				type="tertiary"
				@click="handleAdd()"
				v-can:or="['writeIndividualAddresses', 'writeCompanyAddresses']"
			>
				<crm-icon name="add" color="c2" size="small" />
				{{ $t("add") }}
			</j-button>
		</div>
		<div v-if="!inlineAdd && !inlineUpdate">
			<j-card class="mb2 bg5" v-for="item in addresses" :key="item.id">
				<j-expendable-box>
					<div slot="header" class="p3 fs3 fw7 c2">{{ item.type }}</div>
					<div slot="body">
						<j-list :list="item.fields">
							<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
								<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
									{{ item.type }}
								</div>
								<div class="flex xs-4 xs-left xs-middle lh125">
									{{ item.value }}
								</div>
							</div>
						</j-list>
						<div
							v-if="hasActions"
							class="flex xs-12 bt1 bc7 pt2 pb2 xs-right xs-middle"
						>
							<j-button
								type="tertiary"
								@click="handleUpdate(item)"
								v-can:or="['writeIndividualAddresses', 'writeCompanyAddresses']"
							>
								<crm-icon name="edit" color="c6" />
							</j-button>
							<j-button
								type="tertiary"
								@click="handleDelete(item.id)"
								v-can:or="['writeIndividualAddresses', 'writeCompanyAddresses']"
							>
								<crm-icon name="delete" color="cError" />
							</j-button>
						</div>
					</div>
				</j-expendable-box>
			</j-card>
			<j-card class="bg5 ta2 fs3 p2" v-if="addresses.length === 0">{{
				$t("errors.no_data")
			}}</j-card>
		</div>

		<!-- Add Modal -->
		<core-modal :name="`addAddress${id}`" :headline="$t('add_address')">
			<entity-add
				:id="id"
				for="addresses"
				:config="addConfig"
				@added="afterAdded"
			/>
		</core-modal>

		<!-- Update Modal -->
		<core-modal
			:name="`updateAddress${id}`"
			:headline="$t('update_address')"
			@close="afterUpdated"
		>
			<entity-update
				:id="id"
				for="addresses"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</core-modal>

		<!-- Inline Add -->
		<div v-if="inlineAdd" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("add_address") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineAdd = false"
				/>
			</div>
			<entity-add
				:id="id"
				size="small"
				fsLabel="fs3"
				for="addresses"
				:config="addConfig"
				@added="afterAdded"
			/>
		</div>

		<!-- Inline Update -->
		<div v-if="inlineUpdate" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("update_address") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineUpdate = false"
				/>
			</div>
			<entity-update
				:id="id"
				size="small"
				fsLabel="fs3"
				for="addresses"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</div>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JExpendableBox from "@/components/JExpendableBox/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import EntityAdd from "./EntityAdd"
import EntityUpdate from "./EntityUpdate"
import api from "@/modules/core/api/"
import { countries } from "@/forms/enums.js"

export default {
	props: {
		id: String,
		addresses: Array,
		hasActions: Boolean,
		mode: String
	},

	components: {
		JCard,
		JList,
		JExpendableBox,
		JButton,
		CrmIcon,
		CoreModal,
		EntityAdd,
		EntityUpdate
	},

	data() {
		return {
			inlineAdd: false,
			inlineUpdate: false,
			addConfig: [
				{
					componentName: "j-base-input-text",
					id: "type",
					label: this.$t("type"),
					subheadline: "Ex: Home Address",
					validate: "required"
				},
				{
					componentName: "j-input-object-list",
					id: "fields",
					label: this.$t("fields"),
					subheadline: "Ex: Country: Hong Kong"
				}
			],

			updateId: null,

			updateConfig: null
		}
	},
	methods: {
		async handleDelete(payload) {
			const res = await api.deleteAddress({
				entityId: this.id,
				id: payload
			})

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.$emit("deleted")
		},

		handleAdd() {
			if (this.mode === "modal") this.$modal.show(`addAddress${this.id}`)
			if (this.mode === "inline") {
				this.inlineAdd = !this.inlineAdd
				this.inlineUpdate = false
			}
		},

		handleUpdate(payload) {
			// Set updateId which is id of contact, address, identities, fact etc
			this.updateId = payload.id

			// initialize updateConfig
			this.updateConfig = [
				{
					componentName: "j-base-input-text",
					id: "type",
					label: this.$t("type"),
					validate: "required",
					value: payload.type
				},
				{
					componentName: "j-input-object-list",
					id: "fields",
					label: this.$t("fields"),
					value: payload.fields,
					subheadline: "Ex: Country: Hong Kong"
				}
			]

			// Show update modal
			if (this.mode === "modal") this.$modal.show(`updateAddress${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = true
		},

		afterAdded() {
			if (this.mode === "modal") this.$modal.hide(`addAddress${this.id}`)
			if (this.mode === "inline") this.inlineAdd = false
			this.$emit("added")
		},

		afterUpdated() {
			if (this.mode === "modal") this.$modal.hide(`updateAddress${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = false
			this.$emit("updated")
		}
	}
}
</script>
